import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { SettingsSectionLabel } from "@/components/settings/settings-section-label";
import { QueryKeys } from "@/constants/query-keys";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { UserSchema } from "@/schemas/user-schema";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DateModalSelect from "../../components/date-modal-select";
import SexModalSelect from "../../components/sex-modal-select";
import { SettingModalHeader } from "../../components/setting-modal-header";

interface PersonalInfoPageProps {
  onBack?: () => void;
}

const pillCls =
  "bg-white border border-[#ebe0fb] rounded-[27px] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)] h-[52px] w-full flex items-center px-[18px]";

const PersonalInfoPage = ({ onBack }: PersonalInfoPageProps) => {
  const { user, isLoading } = useGetUser();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating } = useUpdateUser();
  const { showSuccess, showError } = useToast();

  const personalInfo = user?.personalInfo;

  const initialValues: z.infer<typeof UserSchema> = {
    firstName: personalInfo?.firstName ?? "",
    lastName: personalInfo?.lastName ?? "",
    dob: personalInfo?.dob ?? "",
    sex: personalInfo?.sex ?? "male",
  };

  const form = useForm<z.infer<typeof UserSchema>>({
    defaultValues: initialValues,
    resolver: zodResolver(UserSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const didHydrateFormRef = useRef(false);
  useEffect(() => {
    if (isLoading || didHydrateFormRef.current) return;
    reset(initialValues);
    didHydrateFormRef.current = true;
  }, [isLoading, reset, initialValues]);

  async function handleFormSubmit(values: z.infer<typeof UserSchema>) {
    try {
      const validatedValues = UserSchema.parse(values);

      await updateUserAsync({
        firstName: validatedValues.firstName,
        lastName: validatedValues.lastName,
        dob: validatedValues.dob,
        sex: validatedValues.sex,
      });

      await queryClient.invalidateQueries({ queryKey: [QueryKeys.USER] });
      showSuccess("Profile updated successfully");
      reset(validatedValues);
      onBack?.();
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unexpected error occurred");
      }
    }
  }

  const handleDownloadData = () => {
    console.log("Download data requested");
    showSuccess("Data download will be available soon");
  };

  return (
    <div className="min-h-full w-full bg-[#fcfbfb]">
      <SettingModalHeader
        title="Your Profile"
        onBack={onBack}
        onSave={handleSubmit(handleFormSubmit)}
        isSaving={isUpdating}
        showSaveButton
      />

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-[14px] px-9 pt-4 pb-8"
      >
        <TextFieldWithLabel
          control={control}
          name="firstName"
          label="First Name"
          placeholder="Enter your first name"
          error={errors.firstName?.message}
          theme="settings"
        />

        <TextFieldWithLabel
          control={control}
          name="lastName"
          label="Last Name"
          placeholder="Enter your last name"
          error={errors.lastName?.message}
          theme="settings"
        />

        <DateModalSelect
          form={form}
          name="dob"
          label="Date of Birth"
          error={errors.dob?.message}
          theme="settings"
        />

        <SexModalSelect form={form} name="sex" error={errors.sex?.message} theme="settings" />

        <div className="flex flex-col gap-2">
          <SettingsSectionLabel label="Your Data" />
          <button
            type="button"
            onClick={handleDownloadData}
            disabled={isUpdating}
            className={cn(pillCls, "justify-center")}
          >
            <span className={cn(textVariants.inputText, "text-[#620ad5]")}>Download Your Data</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfoPage;
